/**
 * Product Rating Inline JavaScript.
 * Fetches rating data from Reviews.io API and displays it inline in the product page.
 */

class ProductRatingInline {
  constructor() {
    this.ratingElements = document.querySelectorAll('.js-product-rating-inline');

    window.initProductRating = this.init.bind(this);

    if (this.ratingElements.length > 0) {
      this.init();
    }
  }

  /**
   * Initialise the inline rating system.
   */
  async init() {

    /* Added again so that Tagalys can find it */
    this.ratingElements = document.querySelectorAll('.js-product-rating-inline');

    for (const element of this.ratingElements) {
      await this.loadRatingForElement(element);
    }
  }

  /**
   * Load rating data for a specific element.
   * @param {Element} element - The rating element to update.
   */
  async loadRatingForElement(element) {
    try {
      if (!(element instanceof HTMLElement)) return;

      const sku = element.dataset.sku;

      if (!sku) {
        console.warn('ProductRatingInline: No SKU found for rating element');
        return;
      }

      // Get store key from the page
      const storeKey = this.getStoreKey();

      if (!storeKey) {
        console.warn('ProductRatingInline: No store key found for Reviews.io');
        return;
      }

      // Fetch rating data
      const ratingData = await this.fetchRatingData(storeKey, sku);

      if (ratingData) {
        this.updateRatingDisplay(element, ratingData);
      }
    } catch (error) {
      console.error('ProductRatingInline: Error loading rating data:', error);
    }
  }

  /**
   * Fetch rating data from Reviews.io API.
   * @param {string} storeKey - The store key for Reviews.io.
   * @param {string} sku - The product SKU.
   * @returns {Promise<Object|null>} The rating data or null if failed.
   */
  async fetchRatingData(storeKey, sku) {
    const params = new URLSearchParams({
      store: storeKey,
      sku: sku,
      type: 'product_review',
      per_page: '1' // We only need summary data
    });

    const url = `https://api.reviews.io/reviews?${params.toString()}`;

    try {
      const response = await fetch(url);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('ProductRatingInline: API error response:', errorText);
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      return {
        averageRating: parseFloat(data.average_rating) || 0,
        reviewCount: data.review_count || 0
      };
    } catch (error) {
      console.error('ProductRatingInline: Error fetching rating data:', error);
      return null;
    }
  }

  /**
   * Update the rating display with fetched data.
   * @param {Element} element - The rating element to update.
   * @param {Object} ratingData - The rating data from the API.
   */
  updateRatingDisplay(element, ratingData) {
    const { averageRating, reviewCount } = ratingData;

    // Determine if this element is used on a product card.
    const isCardRating = element.classList.contains('product-rating-inline__content--card');

    // If there are no reviews: for cards keep hidden; for PDP hide the link wrapper and exit.
    if (!reviewCount || reviewCount === 0) {
      if (isCardRating) {
        return;
      }
      const pdpLinkWrapper = element.closest('.product-rating-inline');
      if (pdpLinkWrapper instanceof HTMLElement) {
        pdpLinkWrapper.style.display = 'none';
      } else if (element instanceof HTMLElement) {
        element.style.display = 'none';
      }
      return;
    }

    // Show the rating content now that we have data.
    if (element instanceof HTMLElement) {
      element.style.display = 'flex';
    }

    // Update rating value.
    const ratingValue = element.querySelector('.js-rating-value');
    if (ratingValue) {
      ratingValue.textContent = averageRating.toFixed(1);
    } else {
      console.warn('ProductRatingInline: Rating value element not found');
    }

    // Update review count.
    const ratingCount = element.querySelector('.js-rating-count');
    if (ratingCount) {
      const countText = reviewCount === 1 ? '(1 review)' : `(${reviewCount} reviews)`;
      ratingCount.textContent = countText;
    } else {
      console.warn('ProductRatingInline: Rating count element not found');
    }

    // Update stars.
    this.updateStars(element, averageRating);
  }

  /**
   * Update the star display based on the rating.
   * @param {Element} element - The rating element.
   * @param {number} rating - The average rating.
   */
  updateStars(element, rating) {
    const starsContainer = element.querySelector('.js-rating-stars');
    if (!starsContainer) {
      console.warn('ProductRatingInline: Stars container not found');
      return;
    }

    // Clear existing stars
    starsContainer.innerHTML = '';

    // Determine size modifier based on JS hook class (avoid styling classes).
    const starSizeClass = starsContainer.classList.contains('js-rating-stars--card')
      ? 'svg-wrapper--xsmall'
      : 'svg-wrapper--small';

    // Create new stars based on rating
    for (let i = 1; i <= 5; i++) {
      const star = document.createElement('span');
      star.className = `svg-wrapper ${starSizeClass} product-reviews__star-color`;

      if (i <= Math.floor(rating)) {
        // Fully filled star - use the same SVG structure as the product-reviews section.
        star.innerHTML =
          '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.36 2.073a.72.72 0 0 1 1.28 0l1.53 3.002a.68.68 0 0 0 .499.362l3.324.53c.58.091.81.802.396 1.217l-2.38 2.386a.68.68 0 0 0-.19.586l.525 3.33a.719.719 0 0 1-1.036.753l-3-1.528-.074-.032a.678.678 0 0 0-.468 0l-.075.032-2.999 1.528a.719.719 0 0 1-1.036-.752l.526-3.331a.68.68 0 0 0-.19-.586l-2.38-2.386a.719.719 0 0 1 .395-1.218l3.324-.53a.68.68 0 0 0 .5-.361l1.528-3.002Z" fill="currentColor"/></svg>';
      } else if (i === Math.ceil(rating) && rating % 1 !== 0) {
        // Half-filled star (for decimal ratings).
        star.innerHTML =
          '<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.35938 2.07263C7.62608 1.54974 8.37392 1.54974 8.64062 2.07263L10.1699 5.07458C10.2686 5.26809 10.4544 5.40272 10.6689 5.43689L13.9932 5.96619C14.5724 6.05844 14.8029 6.76868 14.3887 7.18396L12.0088 9.5697C11.8556 9.72329 11.7847 9.94128 11.8184 10.1556L12.3438 13.4867C12.4348 14.0662 11.8305 14.5046 11.3076 14.2386L8.30859 12.7113L8.23438 12.6791C8.08307 12.6234 7.91695 12.6234 7.76562 12.6791L7.69141 12.7113L4.69238 14.2386C4.16953 14.5046 3.5652 14.0662 3.65625 13.4867L4.18164 10.1556C4.21528 9.94133 4.14442 9.72332 3.99121 9.5697L1.61133 7.18396C1.19715 6.76868 1.42761 6.05844 2.00684 5.96619L5.33105 5.43689C5.54558 5.40271 5.73136 5.2681 5.83008 5.07458L7.35938 2.07263Z" stroke="#CA3800" stroke-width="0.8" stroke-linejoin="round"/><path d="M8.00035 13.0371C7.95682 13.0371 7.9133 13.0471 7.87339 13.0674L4.87437 14.5947C4.06036 15.0093 3.11894 14.3263 3.26109 13.4238L3.78648 10.0938C3.80037 10.0055 3.77042 9.91576 3.70738 9.85254L1.32749 7.46582C0.683242 6.81942 1.04238 5.71498 1.94371 5.57129L5.2689 5.04199C5.35707 5.02783 5.43343 4.97214 5.47398 4.89258L7.00328 1.8916C7.21086 1.48431 7.6056 1.28014 8.00035 1.28027V13.0371Z" fill="currentColor"/></svg>';
      } else {
        // Empty star - use the same SVG structure as the product-reviews section.
        star.innerHTML =
          '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M7.36 2.073a.72.72 0 0 1 1.28 0l1.53 3.002a.68.68 0 0 0 .499.362l3.324.53c.58.091.81.802.396 1.217l-2.38 2.386a.68.68 0 0 0-.19.586l.525 3.33a.719.719 0 0 1-1.036.753l-3-1.528-.074-.032a.678.678 0 0 0-.468 0l-.075.032-2.999 1.528a.719.719 0 0 1-1.036-.752l.526-3.331a.68.68 0 0 0-.19-.586l-2.38-2.386a.719.719 0 0 1 .395-1.218l3.324-.53a.68.68 0 0 0 .5-.361l1.528-3.002Z" stroke="currentColor" stroke-width=".8" stroke-linejoin="round"/></svg>';
      }

      starsContainer.appendChild(star);
    }
  }

  /**
   * Get the store key from the page.
   * @returns {string|null} The store key or null if not found.
   */
  getStoreKey() {
    // Try to get from product-reviews section if available
    const reviewsSection = document.querySelector('.js-reviews-config');

    if (reviewsSection && reviewsSection.textContent) {
      try {
        const config = JSON.parse(reviewsSection.textContent);
        const storeKey = config.store || null;
        return storeKey;
      } catch (error) {
        console.warn('ProductRatingInline: Could not parse reviews config:', error);
      }
    }

    // Try alternative selectors
    const alternativeConfig = document.querySelector('script.js-reviews-config');
    if (alternativeConfig && alternativeConfig.textContent) {
      try {
        const config = JSON.parse(alternativeConfig.textContent);
        const storeKey = config.store || null;
        return storeKey;
      } catch (error) {
        console.warn('ProductRatingInline: Could not parse alternative config:', error);
      }
    }

    // Try to find any script tag with reviews config
    const allScripts = document.querySelectorAll('script');
    for (const script of allScripts) {
      if (script.textContent && script.textContent.includes('"store"') && script.textContent.includes('"apikey"')) {
        try {
          const config = JSON.parse(script.textContent);
          if (config.store) {
            return config.store;
          }
        } catch {
          // Continue to next script
        }
      }
    }

    console.warn('ProductRatingInline: No store key found in any location');
    return null;
  }
}

// Initialise when DOM is ready
document.addEventListener('DOMContentLoaded', () => {
  // Add a small delay to ensure all sections are loaded
  setTimeout(() => {
    new ProductRatingInline();
  }, 100);
});

// Also initialise if DOM is already loaded
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    setTimeout(() => {
      new ProductRatingInline();
    }, 100);
  });
} else {
  setTimeout(() => {
    new ProductRatingInline();
  }, 100);
}
